import React from 'react';
import { useCollection } from '@cloudscape-design/collection-hooks';
import {
  Table,
  Input,
  Select,
  SpaceBetween,
  Box,
  Button,
  CollectionPreferences,
  Pagination,
} from '@cloudscape-design/components';
import { useLocalStorage } from '../commons/use-local-storage';
import { useColumnWidths } from '../commons/use-column-widths';
import { TableEmptyState, TableNoMatchState } from '../commons/common-components';
import { getTextFilterCounterText } from '../../i18n-strings';

const defaultStatus = { value: '0', label: 'All Status' };

export function BaseTable({
  columnDefinitions,
  items,
  loading,
  statusOptions = [],
  statusFilter,
  setStatusFilter,
  onFetchData,
  preferences,
  setPreferences,
  selectedItems,
  setSelectedItems,
  actions,
  loadMoreIsVisible,
  tableHeader,
  selectionType = "multi"
}) {
  const [columnDefs, saveWidths] = useColumnWidths(
    'React-Table-Base-Widths',
    columnDefinitions
  );

  const { items: filteredItems, actions: collectionActions, filteredItemsCount, collectionProps, filterProps, paginationProps } = 
    useCollection(items, {
      filtering: {
        empty: <TableEmptyState resourceName="Items" />,
        noMatch: (
          <TableNoMatchState onClearFilter={() => collectionActions.setFiltering('')} />
        ),
        filteringFunction: (item, filteringText) => {
          const filteringTextLowerCase = filteringText.toLowerCase();
          return columnDefinitions
            .filter(col => col.filterable !== false)
            .some(col => {
              const value = item[col.id];
              return value && value.toString().toLowerCase().includes(filteringTextLowerCase);
            });
        },
      },
      pagination: { pageSize: preferences?.pageSize || 10 },
      ...(selectionType !== "none" && { selection: {} }),
    });

  const tableProps = {
    ...collectionProps,
    columnDefinitions: columnDefs,
    items: filteredItems,
    loading,
    loadingText: "Loading items",
    variant: "full-page",
    stickyHeader: true,
    resizableColumns: true,
    onColumnWidthsChange: saveWidths,
    wrapLines: preferences?.wrapLines,
    stripedRows: preferences?.stripedRows,
    contentDensity: preferences?.contentDensity,
    header: tableHeader,
    pagination: <Pagination {...paginationProps} />,
    filter: (
      <div className="input-container">
        <div className="input-filter">
          <Input
            data-testid="input-filter"
            type="search"
            value={filterProps.filteringText}
            onChange={event => collectionActions.setFiltering(event.detail.value)}
            placeholder="Search"
          />
        </div>
        {statusOptions.length > 0 && (
          <div className="select-filter">
            <Select
              data-testid="status-filter"
              options={statusOptions}
              selectedOption={statusFilter}
              onChange={event => setStatusFilter(event.detail.selectedOption)}
              expandToViewport={true}
            />
          </div>
        )}
        <div aria-live="polite">
          {filterProps.filteringText && (
            <span className="filtering-results">
              {getTextFilterCounterText(filteredItemsCount)}
            </span>
          )}
        </div>
      </div>
    ),
    footer: onFetchData && loadMoreIsVisible && (
      <Box textAlign="center">
        <SpaceBetween direction="horizontal" size="xs">
          <Button onClick={onFetchData} disabled={!loadMoreIsVisible}>
            Load More
          </Button>
        </SpaceBetween>
      </Box>
    )
  };

  if (selectionType !== "none") {
    tableProps.selectionType = selectionType;
    tableProps.selectedItems = selectedItems;
    tableProps.onSelectionChange = ({ detail }) => {
      setSelectedItems(detail.selectedItems);
      console.log(selectedItems);
    };
  }

  return <Table {...tableProps} />;
}

export default BaseTable;